import { formatMoney } from '../../../lib/format';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { totalCostLines } from '../vehicles-display';

export interface TotalCostCardProps {
  outputs: Readonly<Record<string, number | null>>;
  inputs: Readonly<Record<string, number>>;
}

/**
 * The "Projected total cost" card with its own arithmetic (business review 2026-09-23, F2): the figure,
 * and under "How is this calculated?" the purchase price and every projected cost line that makes it up.
 */
export const TotalCostCard = ({ outputs, inputs }: TotalCostCardProps) => {
  const { t } = useI18nTranslation();
  const lines = totalCostLines(outputs, inputs);

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('vehicles.projectedTotalCost')}</p>
      <p className="mt-2 text-xl font-semibold text-card-foreground">
        <span
          dir="ltr"
          className="tabular-nums"
        >
          {formatMoney(outputs.total ?? null)}
        </span>
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{t('vehicles.projectedTotalCostHint')}</p>
      {lines.length === 0 ? null : (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs font-medium text-primary">{t('vehicles.howTotalIsCalculated')}</summary>
          <dl
            className="mt-2 space-y-1 text-xs"
            data-testid="total-cost-lines"
          >
            {lines.map((line, index) => (
              <div
                key={line.id}
                className="flex items-baseline justify-between gap-3"
              >
                <dt className="text-muted-foreground">
                  {index === 0 ? '' : '+ '}
                  {t(`estimates.costLines.${line.id}`)}
                </dt>
                <dd
                  dir="ltr"
                  className="tabular-nums text-foreground"
                >
                  {formatMoney(line.amount, { exact: true })}
                </dd>
              </div>
            ))}
            <div className="flex items-baseline justify-between gap-3 border-t border-border pt-1 font-semibold">
              <dt className="text-foreground">= {t('vehicles.projectedTotalCost')}</dt>
              <dd
                dir="ltr"
                className="tabular-nums text-foreground"
              >
                {formatMoney(outputs.total ?? null, { exact: true })}
              </dd>
            </div>
          </dl>
        </details>
      )}
    </div>
  );
};
